import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import { Fade } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import HttpsIcon from '@mui/icons-material/Https';
import { useAdminLanguage } from './AdminI18n';
import Info from './Info';

// Modes de restriction possibles
const RESTRICTION_MODES = {
  ALWAYS: 'always',    // Toujours restreint (non modifiable par l'utilisateur)
  DEFAULT: 'default',  // Restreint par défaut (modifiable)
  NONE: 'none'         // Non restreint par défaut (modifiable)
};

const RestrictionAdmin = () => {
  const { t } = useAdminLanguage();
  const [restrictionMode, setRestrictionMode] = useState(RESTRICTION_MODES.NONE);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings');
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        const data = await response.json();
        // Migration: supporter l'ancien format boolean et le nouveau format string
        if (data.restrictionMode) {
          setRestrictionMode(data.restrictionMode);
        } else if (data.defaultRestricted === true) {
          setRestrictionMode(RESTRICTION_MODES.DEFAULT);
        } else {
          setRestrictionMode(RESTRICTION_MODES.NONE);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des paramètres:', err);
        setError('Une erreur est survenue lors du chargement des paramètres.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError(null);

    try {
      const token = sessionStorage.getItem('authToken');
      // Envoyer le nouveau format restrictionMode et garder defaultRestricted pour compatibilité
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          restrictionMode,
          defaultRestricted: restrictionMode !== RESTRICTION_MODES.NONE 
        }),
      });

      if (response.ok) {
        setMessage(t('Paramètre de restriction mis à jour avec succès !'));
      } else {
        const data = await response.json();
        setError(data.error || t('Erreur lors de la mise à jour du paramètre.'));
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError(t('Erreur lors de la communication avec le serveur.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box component="form" onSubmit={handleSubmit} autoComplete="off">
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Restriction par défaut')}
          </Typography>
          <Info type="restriction" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <Typography variant="body1" align="center" sx={{ mb: 4, color: 'text.secondary' }}>
                {t('Définissez si les nouveaux documents doivent être restreints par défaut lors de leur création.')}
              </Typography>

              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 3,
                  flexWrap: 'wrap',
                  mb: 4
                }}
              >
                {/* Option: Documents TOUJOURS restreints (non modifiable) */}
                <Card 
                  sx={{ 
                    width: 280,
                    cursor: 'pointer',
                    border: restrictionMode === RESTRICTION_MODES.ALWAYS ? 3 : 1,
                    borderColor: restrictionMode === RESTRICTION_MODES.ALWAYS ? 'error.main' : 'grey.300',
                    transition: 'all 0.3s',
                    '&:hover': { 
                      borderColor: 'error.main',
                      boxShadow: 3
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => setRestrictionMode(RESTRICTION_MODES.ALWAYS)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <HttpsIcon 
                        sx={{ 
                          fontSize: 64, 
                          mb: 2, 
                          color: restrictionMode === RESTRICTION_MODES.ALWAYS ? 'error.main' : 'grey.500' 
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: restrictionMode === RESTRICTION_MODES.ALWAYS ? 'bold' : 'normal',
                          color: restrictionMode === RESTRICTION_MODES.ALWAYS ? 'error.main' : 'text.primary'
                        }}
                      >
                        {t('Les documents sont toujours restreints')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('L\'utilisateur ne pourra pas décocher la case "Restreindre l\'accès"')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>

                {/* Option: Documents restreints par défaut */}
                <Card 
                  sx={{ 
                    width: 280,
                    cursor: 'pointer',
                    border: restrictionMode === RESTRICTION_MODES.DEFAULT ? 3 : 1,
                    borderColor: restrictionMode === RESTRICTION_MODES.DEFAULT ? 'primary.main' : 'grey.300',
                    transition: 'all 0.3s',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      boxShadow: 3
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => setRestrictionMode(RESTRICTION_MODES.DEFAULT)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <LockIcon 
                        sx={{ 
                          fontSize: 64, 
                          mb: 2, 
                          color: restrictionMode === RESTRICTION_MODES.DEFAULT ? 'primary.main' : 'grey.500' 
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: restrictionMode === RESTRICTION_MODES.DEFAULT ? 'bold' : 'normal',
                          color: restrictionMode === RESTRICTION_MODES.DEFAULT ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {t('Les documents sont initialement restreints')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('La case "Restreindre l\'accès" sera cochée par défaut mais modifiable par l\'utilisateur')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>

                {/* Option: Documents non restreints par défaut */}
                <Card 
                  sx={{ 
                    width: 280,
                    cursor: 'pointer',
                    border: restrictionMode === RESTRICTION_MODES.NONE ? 3 : 1,
                    borderColor: restrictionMode === RESTRICTION_MODES.NONE ? 'primary.main' : 'grey.300',
                    transition: 'all 0.3s',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      boxShadow: 3
                    },
                  }}
                >
                  <CardActionArea 
                    onClick={() => setRestrictionMode(RESTRICTION_MODES.NONE)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <LockOpenIcon 
                        sx={{ 
                          fontSize: 64, 
                          mb: 2, 
                          color: restrictionMode === RESTRICTION_MODES.NONE ? 'primary.main' : 'grey.500' 
                        }} 
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: restrictionMode === RESTRICTION_MODES.NONE ? 'bold' : 'normal',
                          color: restrictionMode === RESTRICTION_MODES.NONE ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {t('Les documents ne sont pas initialement restreints')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('La case "Restreindre l\'accès" sera décochée par défaut mais modifiable par l\'utilisateur')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={20} />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: 6 },
                  maxWidth: '300px',
                  mx: 'auto',
                  display: 'block',
                  mt: 3,
                }}
              >
                {submitting ? t('Enregistrement...') : t('Enregistrer')}
              </Button>

              {message && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default RestrictionAdmin;
